import { LoadingButton } from "@mui/lab";
import { Alert, Autocomplete, Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, InputAdornment, List, ListItem, ListItemAvatar, ListItemButton, ListItemSecondaryAction, ListItemText, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import LoadingProgress from "../app/LoadingProgress";
import { useTranslation } from "react-i18next";
import $api from "../http";
import { useAppConext } from "../context/AppContext";
import { useSnackbar } from "notistack";
import { NewReleases, RadioButtonChecked, RadioButtonUnchecked, Warning, Workspaces } from "@mui/icons-material";

const WidgetTransferDialog = (props) => {
    const { open, onClose, onSubmit, currentItem } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [items, setItems] = useState([]);
    const [newValue, setNewValue] = useState(null);
    const { user } = useAppConext();
    const [defaultItem, setDefaultItem] = useState(null);
    const [deleteWidgetData, setDeleteWidgetData] = useState(false);
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [newWsName, setNewWsName] = useState('');
    const [errors, setErrors] = useState({});

    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = () => {
        setIsSaving(true);
        setErrors(false);
        $api.post('/widget/transfer', {
            widget: currentItem.uid,
            user_id: newValue.id,
            ws: selectedWorkspace, 
            newWsName: newWsName,
            deleteWidgetData: deleteWidgetData ? 1 : 0
        })
            .catch((err) => {
                if (err?.response?.status === 422) {
                    setErrors(err?.response?.data);
                }
                enqueueSnackbar(err?.response?.data?.message || t('Error transfering widget'), {
                    variant: 'error'
                });
            })
            .then((res) => {
                if (res) {
                    onSubmit();
                    setIsLoading(true);
                    enqueueSnackbar(t('Widget transfered'), {
                        variant: 'success'
                    });
                }
            })
            .finally(() => {
                setIsSaving(false);
            })
    };

    const loadContent = () => {
        $api.get('/user/get-clients')
            .catch((err) => {

            })
            .then((res) => {
                if (res) {
                    setItems([
                        {
                            id: user.id,
                            name: user.name,
                            email: user.email,
                            image: user.image
                        },
                        ...res.data
                    ]);
                }
            })
            .finally(() => {
                setIsLoading(false);
            })
    };

    const loadWorkspaces = (id) => {
        $api.get(`/workspace/get-by-user?id=${id}`)
            .catch((err) => {

            })
            .then((res) => {
                setWorkspaces(res.data);
                if (id === currentItem.user_id) {
                    setSelectedWorkspace(currentItem.workspace);
                }
            })
            .finally(() => {

            })
    };

    const handleChange = (e, v) => {
        setNewValue(v);
        setSelectedWorkspace(null);
        loadWorkspaces(v?.id);
    };

    useEffect(() => {
        const d = items.filter((i) => i.id === currentItem.user_id);
        if (d.length === 1) {
            setDefaultItem(d[0]);
            loadWorkspaces(d[0].id);
            setSelectedWorkspace(currentItem.workspace);
            setNewValue(d[0]);
        }
    }, [items]);

    useEffect(() => {
        if (open) {
            loadContent();
            setNewWsName('');
        }
        setErrors({});
    }, [open]);

    useEffect(() => {
        setErrors({});
    }, [selectedWorkspace]);

    return (
        <Dialog maxWidth="sm" fullWidth open={open} onClose={onClose}>
            <DialogTitle>
                {t('Transfer widget')}
            </DialogTitle>
            <DialogContent>
                <LoadingProgress open={isLoading} />
                <Alert severity="info" sx={{ mt: 2 }}>
                    {t('Widget configuration and settings will be transfered to the selected account.')}
                </Alert>
                <FormGroup>
                    <Autocomplete
                        sx={{ mt: 2 }}
                        options={items}
                        autoHighlight
                        fullWidth
                        getOptionLabel={(i) => i.name || `${i.firstname} ${i.lastname}`}
                        onChange={handleChange}
                        value={newValue}
                        renderOption={(props, i) => (
                            <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                <Avatar src={i.image} sx={{ mr: 1 }} />
                                <Box>
                                    <Typography>
                                        {i.name || `${i.firstname} ${i.lastname}`}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {i.email}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                label={t('Target account')}
                                inputProps={{
                                    ...params.inputProps,
                                    autoComplete: 'new-password', // disable autocomplete and autofill
                                }}
                            />
                        )}
                    />
                </FormGroup>
                {(newValue !== null) ? (
                    <FormGroup sx={{ mt: 1, mb: 2 }}>
                        <List dense>
                            {(workspaces.map((i) => (
                                <ListItemButton selected={selectedWorkspace === i.guid} key={i.guid} sx={{ borderRadius: 1, my: '2px' }} onClick={() => setSelectedWorkspace(i.guid)}>
                                    <ListItemAvatar>
                                        <Avatar src={i.image}>
                                            <Workspaces />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primaryTypographyProps={{
                                            noWrap: true
                                        }}
                                        secondaryTypographyProps={{
                                            noWrap: true
                                        }}
                                        primary={i.title}
                                        secondary={i.description}
                                    />
                                    <ListItemSecondaryAction>
                                        {selectedWorkspace === i.guid ? (
                                            <RadioButtonChecked color="primary" sx={{ display: 'block', my: 'auto' }} />
                                        ) : (
                                            <RadioButtonUnchecked color="primary" sx={{ display: 'block', my: 'auto' }} />
                                        )}
                                    </ListItemSecondaryAction>
                                </ListItemButton>
                            )))}
                            <ListItemButton selected={selectedWorkspace === 'new'} sx={{ borderRadius: 1, my: '2px' }} onClick={() => setSelectedWorkspace('new')}>
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                                        <NewReleases sx="primary" />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    disableTypography
                                    primary={(
                                        <TextField
                                            sx={{
                                                minWidth: 320
                                            }}
                                            size="small"
                                            error={!!errors?.newWsName}
                                            label={t('Create new workspace')}
                                            placeholder={t('Workspace name')}
                                            value={newWsName}
                                            onChange={(e) => setNewWsName(e.target.value)}
                                            InputProps={{
                                                endAdornment: (
                                                    ((!!errors.newWsName) && (
                                                        <InputAdornment position="end">
                                                            <Tooltip title={errors.newWsName}>
                                                                <Warning sx={{ cursor: 'pointer' }} color="error" />
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    ))
                                                )
                                            }}
                                        />
                                    )}
                                />
                                <ListItemSecondaryAction>
                                    {selectedWorkspace === 'new' ? (
                                        <RadioButtonChecked color="primary" sx={{ display: 'block', my: 'auto' }} />
                                    ) : (
                                        <RadioButtonUnchecked color="primary" sx={{ display: 'block', my: 'auto' }} />
                                    )}
                                </ListItemSecondaryAction>
                            </ListItemButton>
                        </List>
                    </FormGroup>
                ) : (
                    <FormGroup sx={{ mt: 1, mb: 2 }}>
                        <Alert severity="warning">
                            {t('Please select the target account')}
                        </Alert>
                    </FormGroup>
                )}
                
                <FormGroup sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={<Switch
                            checked={deleteWidgetData} name="deleteData"
                            onChange={(e) => setDeleteWidgetData(!!e.target.checked)}
                        />}
                        label={t('Delete widget-related data')}
                    />
                </FormGroup>
                {(deleteWidgetData === true) ? (
                    <Alert severity="error">
                        {t('Widget-related data like conversations, form data, and contacts will be lost.')}
                    </Alert>
                ) : (
                    <Alert severity="warning">
                        {t('Widget-related data like conversations, form data, and contacts will be trasfered to the selected account.')}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {t('Cancel')}
                </Button>
                <LoadingButton disabled={newValue === null || selectedWorkspace === null || currentItem.workspace === selectedWorkspace} variant="contained" disableElevation loading={isSaving} color="error" onClick={handleSubmit}>
                    {t('Confirm')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default WidgetTransferDialog;